import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
  Divider,
  Stack,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SchoolIcon from "@mui/icons-material/School";

// ---------- helpers ----------

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const stringToColor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 45%)`;
};

const percentColor = (pct) => {
  if (pct >= 75) return "success";
  if (pct >= 40) return "warning";
  return "error";
};

const gradeColor = (grade = "") => {
  const g = grade.toUpperCase();
  if (g.startsWith("A")) return { bg: "#E8F5E9", color: "#2E7D32" };
  if (g.startsWith("B")) return { bg: "#E3F2FD", color: "#1565C0" };
  if (g.startsWith("C")) return { bg: "#FFF8E1", color: "#F9A825" };
  return { bg: "#FFEBEE", color: "#C62828" };
};

const RankBadge = ({ rank }) => {
  const medals = { 1: "🥇", 2: "🥈", 3: "🥉" };
  if (medals[rank]) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>
        {medals[rank]}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#EEF1F6",
        color: "#455A64",
        fontWeight: 700,
        fontSize: 13,
        mx: "auto",
      }}
    >
      {rank}
    </Box>
  );
};

const TRSheet = () => {
  // Dropdown data
  const [ddlSession, setDdlSession] = useState([]);
  const [ddlClass, setDdlClass] = useState([]);
  const [ddlSection, setDdlSection] = useState([]);
  const [ddlExamType, setDdlExamType] = useState([]);

  // Filter state (used to build the ranking request)
  const [filters, setFilters] = useState({
    sessionId: 0,
    classId: 0,
    sectionId: 0,
    examTypeId: 0,
  });

  // Ranking data (ClassRanking returns a plain array — no server pagination)
  const [rankingData, setRankingData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  // Details dialog state (ClassDetailReport, fetched per student)
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailData, setDetailData] = useState({ summaries: [], details: [] });
  const [activeExamTab, setActiveExamTab] = useState(0);

  const apiUrl = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem("token");

  const showMessage = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ---- Fetch dropdowns ----

 const fetchDdlSession = useCallback(async () => {
  try {
    const response = await fetch(`${apiUrl}/ClassPromotion/GetFinancialYear`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({}),
    });
    if (!response.ok) throw new Error(`Failed to fetch sessions: ${response.status}`);
    const data = await response.json();
    const sessions = Array.isArray(data) ? data : [];
    setDdlSession(sessions);

    // Auto-select the latest session (highest financialYearID)
    if (sessions.length > 0) {
      const latest = sessions.reduce((max, s) =>
        s.financialYearID > max.financialYearID ? s : max
      );
      setFilters((prev) => ({ ...prev, sessionId: latest.financialYearID }));
    }
  } catch (error) {
    console.error("Error fetching sessions:", error);
    showMessage("Failed to fetch sessions", "error");
  }
}, [apiUrl, token]);

  const fetchDdlClass = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(`Failed to fetch classes: ${response.status}`);
      const data = await response.json();
      setDdlClass(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching classes:", error);
      showMessage("Failed to fetch classes", "error");
    }
  }, [apiUrl, token]);

  const fetchDdlSection = useCallback(
    async (classId) => {
      try {
        const response = await fetch(`${apiUrl}/Exam/ddlSection_clsId`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ classId, teacherId: 0 }),
        });
        if (!response.ok) throw new Error(`Failed to fetch sections: ${response.status}`);
        const data = await response.json();
        setDdlSection(data && data.data === null ? [] : Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching sections:", error);
        showMessage("Failed to fetch sections", "error");
      }
    },
    [apiUrl, token]
  );

  // NOTE: classTypeId is assumed to come from the selected class object
  // (ddlClass item). Adjust the field name below if your API names it
  // differently.
  const fetchDdlExamType = useCallback(
    async (classTypeId) => {
      try {
        const response = await fetch(`${apiUrl}/ExaminationSubCategory/ExamTypeddl`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ classTypeId }),
        });
        if (!response.ok) throw new Error(`Failed to fetch exam types: ${response.status}`);
        const data = await response.json();
        setDdlExamType(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching exam types:", error);
        showMessage("Failed to fetch exam types", "error");
      }
    },
    [apiUrl, token]
  );

  useEffect(() => {
    fetchDdlSession();
    fetchDdlClass();
  }, [fetchDdlSession, fetchDdlClass]);

  // When class changes: reset section/exam type, fetch new sections + exam types
  useEffect(() => {
    if (filters.classId) {
      fetchDdlSection(filters.classId);

      const selectedClassObj = ddlClass.find((c) => c.classId === filters.classId);
      const classTypeId = selectedClassObj?.classId ?? 0;
      fetchDdlExamType(classTypeId);
    } else {
      setDdlSection([]);
      setDdlExamType([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.classId]);

  // ---- Handlers ----

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "classId" ? { sectionId: 0, examTypeId: 0 } : {}),
    }));
  };

  // ClassRanking returns a plain array of students — no pagination envelope.
  const fetchClassRanking = async (activeFilters) => {
    const body = {
      sessionId: activeFilters.sessionId,
      examTypeId: activeFilters.examTypeId ?? 0,
      classId: activeFilters.classId,
      sectionId: activeFilters.sectionId ?? 0,
    };

    const response = await fetch(`${apiUrl}/Result/ClassRanking`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error(`Failed to load ranking: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  };

  const handleSearch = async () => {
    if (!filters.sessionId) {
      showMessage("Please select a session", "warning");
      return;
    }
    if (!filters.classId) {
      showMessage("Please select a class", "warning");
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    try {
      const data = await fetchClassRanking(filters);
      setRankingData(data);
      setPage(0);
      if (data.length === 0) showMessage("No ranking data found for this selection", "info");
    } catch (error) {
      console.error("Error fetching class ranking:", error);
      showMessage("Failed to load ranking data", "error");
      setRankingData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Purely client-side paging since ClassRanking returns everything at once.
  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rankingData.slice(start, start + rowsPerPage);
  }, [rankingData, page, rowsPerPage]);

  const downloadClassExcel = async () => {
    if (!filters.sessionId) {
      showMessage("Please select a session", "warning");
      return;
    }
    if (!filters.classId) {
      showMessage("Please select a class", "warning");
      return;
    }

    setIsDownloading(true);
    try {
      const body = {
        sessionId: filters.sessionId,
        classId: filters.classId,
        sectionId: filters.sectionId ?? 0,
        examTypeId: filters.examTypeId ?? 0,
        studentId: 0,
      };

      const response = await fetch(`${apiUrl}/Result/ClassDetailExcel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Failed to download Excel: ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ClassExamReport_${body.sessionId}_${body.classId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showMessage("TR Excel downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading Excel:", error);
      showMessage("Failed to download TR Excel", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  // ---- Student detail dialog (ClassDetailReport, scoped to one student) ----
  // NOTE: the endpoint's sample response wasn't scoped to a single student,
  // but its Excel sibling (ClassDetailExcel) accepts a studentId, so we pass
  // one here too. If the backend ignores it, filter defensively below.

  const openStudentDetails = async (student) => {
    setSelectedStudent(student);
    setDetailOpen(true);
    setDetailLoading(true);
    setActiveExamTab(0);
    try {
      const body = {
        sessionId: filters.sessionId,
        classId: filters.classId,
        sectionId: filters.sectionId ?? 0,
        examTypeId: filters.examTypeId ?? 0,
        studentId: student.studentId,
        pageNumber: 1,
        pageSize: 100,
      };

      const response = await fetch(`${apiUrl}/Result/ClassDetailReport`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Failed to load student details: ${response.status}`);
      const data = await response.json();

      const summaries = (data.summaries || []).filter((s) => s.studentId === student.studentId);
      const details = (data.details || []).filter((d) => d.studentId === student.studentId);

      setDetailData({ summaries, details });
    } catch (error) {
      console.error("Error fetching student details:", error);
      showMessage("Failed to load student details", "error");
      setDetailData({ summaries: [], details: [] });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeStudentDetails = () => {
    setDetailOpen(false);
    setSelectedStudent(null);
    setDetailData({ summaries: [], details: [] });
  };

  // Group detail rows by examType for tabs
  const examGroups = useMemo(() => {
    const map = new Map();
    detailData.details.forEach((row) => {
      const key = row.examType || "Exam";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });
    return Array.from(map.entries()).map(([examType, rows]) => ({ examType, rows }));
  }, [detailData.details]);

  // The `summaries` array from ClassDetailReport doesn't carry an examType or
  // subjectName on each row, so it can't be labeled reliably. Instead, derive
  // exam-wise totals directly from `details` (which has subject + exam info
  // on every row) so the totals and subject names always line up.
  const examTotals = useMemo(() => {
    return examGroups.map((g) => {
      const obtained = g.rows.reduce((sum, r) => sum + (r.obtainedMarks || 0), 0);
      const max = g.rows.reduce((sum, r) => sum + (r.maxMarks || 0), 0);
      const pct = max ? +((obtained / max) * 100).toFixed(2) : 0;
      const subjects = g.rows.map((r) => r.subjectName);
      return { examType: g.examType, obtained, max, pct, subjects };
    });
  }, [examGroups]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth="xl" sx={{ padding: "20px" }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #0B1F40 0%, #16305E 60%, #1E4488 100%)",
          borderRadius: "12px",
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <SchoolIcon sx={{ color: "#fff", mr: 1.5, fontSize: 32 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>
              Class Exam Ranking & TR Report
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              View student rankings and download excel reports
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Paper elevation={2} sx={{ padding: "24px", marginTop: "20px", borderRadius: "12px" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Session</InputLabel>
              <Select value={filters.sessionId} label="Session" onChange={handleFilterChange("sessionId")}>
                <MenuItem value={0} disabled>
                  Select Session
                </MenuItem>
                {ddlSession.map((s) => (
                  <MenuItem key={s.financialYearID} value={s.financialYearID}>
                    {s.finanacialYear}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Class</InputLabel>
              <Select value={filters.classId} label="Class" onChange={handleFilterChange("classId")}>
                <MenuItem value={0} disabled>
                  Select Class
                </MenuItem>
                {ddlClass.map((c) => (
                  <MenuItem key={c.classId} value={c.classId}>
                    {c.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Section</InputLabel>
              <Select
                value={filters.sectionId}
                label="Section"
                onChange={handleFilterChange("sectionId")}
                disabled={!filters.classId}
              >
                <MenuItem value={0}>All Sections</MenuItem>
                {ddlSection.map((sec) => (
                  <MenuItem key={sec.sectionId} value={sec.sectionId}>
                    {sec.sectionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Exam Type</InputLabel>
              <Select
                value={filters.examTypeId}
                label="Exam Type"
                onChange={handleFilterChange("examTypeId")}
                disabled={!filters.classId}
              >
                <MenuItem value={0}>All Exams</MenuItem>
                {ddlExamType.map((et) => (
                  <MenuItem key={et.examCategoryId} value={et.examCategoryId}>
                    {et.examType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2.4}>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={isLoading}
                sx={{ borderRadius: "8px", flex: 1 }}
              >
                Search
              </Button>
              <Tooltip title="Download full class TR Excel">
                <span>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={downloadClassExcel}
                    disabled={isDownloading || !filters.sessionId || !filters.classId}
                    sx={{ borderRadius: "8px", minWidth: "48px", px: 1.5 }}
                  >
                    {isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ padding: "12px", marginTop: "20px", borderRadius: "12px", overflow: "hidden" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "60px" }}>
            <CircularProgress />
          </Box>
        ) : !hasSearched ? (
          <Box sx={{ textAlign: "center", padding: "50px 20px" }}>
            <EmojiEventsIcon sx={{ fontSize: 48, color: "#CFD8DC", mb: 1 }} />
            <Typography color="textSecondary">
              Select session and class, then click Search to view class ranking.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 620 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }} align="center">
                      Rank
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }}>Roll No</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }}>Admission No</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }}>Parents</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }} align="right">
                      Marks
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9", minWidth: 160 }}>Overall %</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }} align="center">
                      Grade
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }} align="center">
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }} align="center">
                      Details
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                        No ranking data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((s) => {
                      const grade = gradeColor(s.totalGrade);
                      return (
                        <TableRow
                          key={s.studentId}
                          hover
                          sx={{ "&:hover": { bgcolor: "#FAFBFC" } }}
                        >
                          <TableCell align="center">
                            <RankBadge rank={s.rank} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                              <Avatar
                                sx={{
                                  width: 34,
                                  height: 34,
                                  fontSize: 13,
                                  bgcolor: stringToColor(s.studentName || ""),
                                }}
                              >
                                {getInitials(s.studentName)}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {s.studentName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{s.rollNo}</TableCell>
                          <TableCell>{s.admissionNo}</TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block" color="text.secondary">
                              F: {s.fathersName}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              M: {s.mothersName}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {s.totalObtainedMarks}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              / {s.totalMaxMarks}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(s.overallPercentage, 100)}
                                color={percentColor(s.overallPercentage)}
                                sx={{ flex: 1, height: 7, borderRadius: 5, bgcolor: "#EEF1F6" }}
                              />
                              <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 40 }}>
                                {s.overallPercentage}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={s.totalGrade}
                              size="small"
                              sx={{ bgcolor: grade.bg, color: grade.color, fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={s.isPass ? "Pass" : "Fail"}
                              size="small"
                              sx={{
                                backgroundColor: s.isPass ? "#E8F5E9" : "#FFEBEE",
                                color: s.isPass ? "#2E7D32" : "#C62828",
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View subject-wise details">
                              <IconButton size="small" color="primary" onClick={() => openStudentDetails(s)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={rankingData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* ---- Student Detail Dialog ---- */}
      <Dialog open={detailOpen} onClose={closeStudentDetails} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #0B1F40 0%, #1E4488 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}>
              {getInitials(selectedStudent?.studentName)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedStudent?.studentName}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Roll No {selectedStudent?.rollNo} &nbsp;•&nbsp; {selectedStudent?.admissionNo}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={closeStudentDetails} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "50px" }}>
              <CircularProgress />
            </Box>
          ) : examGroups.length === 0 ? (
            <Box sx={{ textAlign: "center", padding: "40px" }}>
              <Typography color="textSecondary">No subject-wise details available.</Typography>
            </Box>
          ) : (
            <>
              <Tabs
                value={activeExamTab}
                onChange={(e, v) => setActiveExamTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
              >
                {examGroups.map((g, idx) => (
                  <Tab key={g.examType} label={g.examType} value={idx} />
                ))}
              </Tabs>

              <Box sx={{ p: 2.5 }}>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "10px" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }}>Subject</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }}>Sub Exam</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }} align="right">
                          Marks
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }} align="right">
                          %
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }} align="center">
                          Grade
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F4F6F9" }} align="center">
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {examGroups[activeExamTab]?.rows.map((row) => {
                        const grade = gradeColor(row.grade);
                        return (
                          <TableRow key={row.resultId} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{row.subjectName}</TableCell>
                            <TableCell>{row.subExam || "--"}</TableCell>
                            <TableCell align="right">
                              {row.obtainedMarks} / {row.maxMarks}
                            </TableCell>
                            <TableCell align="right">{row.percentage}%</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.grade}
                                size="small"
                                sx={{ bgcolor: grade.bg, color: grade.color, fontWeight: 700 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.resultStatus}
                                size="small"
                                sx={{
                                  bgcolor: row.resultStatus === "Pass" ? "#E8F5E9" : "#FFEBEE",
                                  color: row.resultStatus === "Pass" ? "#2E7D32" : "#C62828",
                                  fontWeight: 700,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {examTotals.length > 0 && (
                  <>
                    <Divider sx={{ my: 2.5 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Exam-wise Totals
                    </Typography>
                    <Grid container spacing={1.5}>
                      {examTotals.map((et) => (
                        <Grid item xs={12} sm={6} md={4} key={et.examType}>
                          <Paper
                            variant="outlined"
                            sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#FAFBFC", height: "100%" }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {et.examType}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                              {et.obtained} / {et.max}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(et.pct, 100)}
                                color={percentColor(et.pct)}
                                sx={{ flex: 1, height: 6, borderRadius: 5, bgcolor: "#EEF1F6" }}
                              />
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {et.pct}%
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                              {et.subjects.map((subj, i) => (
                                <Chip
                                  key={`${subj}-${i}`}
                                  label={subj}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: 11, height: 22 }}
                                />
                              ))}
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeStudentDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TRSheet;